import { type BaseError, ErrorCodes, MetaErrorCodes } from '../models/BaseError'

const codeMessages: { [key in ErrorCodes]: string } = {
  [ErrorCodes.ApplicationCrash]: 'errors.ApplicationCrashed',
  [ErrorCodes.Custom]: '',
  [ErrorCodes.Conflict]: 'errors.Conflict',
  [ErrorCodes.DataValidation]: 'errors.DataIsNotValid',
  [ErrorCodes.Forbidden]: 'errors.YouDoNotHaveAccess',
  [ErrorCodes.InvalidValidation]: 'errors.DataIsNotValid',
  [ErrorCodes.NotFound]: 'errors.NotFound',
  [ErrorCodes.RateLimit]: 'errors.LimitOfRetriesExceeded',
  [ErrorCodes.ResourceDependency]: 'errors.ResourceDependency',
  [ErrorCodes.ServerCommunication]: 'errors.CannotCommunicateWithServer',
  [ErrorCodes.ResetPassword]: 'errors.FailedResetPassword',
  [ErrorCodes.CompanyInvalidData]: 'errors.DataIsNotValid',
  [ErrorCodes.WeakPassword]: 'errors.WeakPassword',
  [ErrorCodes.CompromisedPassword]: 'errors.CompromisedPassword',
  [ErrorCodes.RequestCanceled]: '',
  [ErrorCodes.Unknown]: 'errors.Unknown'
}

export const metaMessages: { [key in MetaErrorCodes]: string } = {
  [MetaErrorCodes.ClosedStore]: 'errors.ClosedStore',
  [MetaErrorCodes.Empty]: 'errors.EmptyFieldError',
  [MetaErrorCodes.ExpectedValue]: 'errors.NotIsTheExpectedValue',
  [MetaErrorCodes.Forbidden]: 'errors.Forbidden',
  [MetaErrorCodes.Inactive]: 'errors.Inactive',
  [MetaErrorCodes.LimitExceeded]: 'errors.LimitExceeded',
  [MetaErrorCodes.Minimum]: 'errors.MinimumLimitNotReched',
  [MetaErrorCodes.NeedAnotherField]: 'errors.NeedAnotherField',
  [MetaErrorCodes.NotFound]: 'errors.NotFound',
  [MetaErrorCodes.RegistryLimitExceeded]: 'errors.RegistryLimitExceeded',
  [MetaErrorCodes.Unique]: 'errors.AlreadySignedUp',
  [MetaErrorCodes.Unknown]: 'errors.DataIsNotValidShortMessage',
  [MetaErrorCodes.WrongFormat]: 'errors.DataIsInWrongFormat',
  [MetaErrorCodes.WrongValue]: 'errors.WrongValue',
  [MetaErrorCodes.ProductCategoryParentRule]: 'errors.ProductCategoryParentRule',
  [MetaErrorCodes.DuplicateValue]: 'errors.DuplicateValue',
  [MetaErrorCodes.CheckPasswordRule]: 'errors.CheckPasswordRule',
  [MetaErrorCodes.CheckAdministratorProfileRule]: 'errors.CheckAdministratorProfileRule',
  [MetaErrorCodes.In]: 'errors.In',
  [MetaErrorCodes.CannotUpload]: 'errors.CannotUpload',
  [MetaErrorCodes.CertificateDate]: 'errors.CertificateDate',
  [MetaErrorCodes.CertificatePassword]: 'errors.CertificatePassword',
  [MetaErrorCodes.CertificateDocument]: 'errors.CertificateDocument',
  [MetaErrorCodes.CertificatePrivateKey]: 'errors.CertificatePrivateKey',
  [MetaErrorCodes.AlreadyExists]: 'errors.AlreadyExists',
  [MetaErrorCodes.GroupIcmsNotBelongsToCompany]: 'errors.GroupIcmsNotBelongsToCompany',
  [MetaErrorCodes.GroupIpiNotBelongsToCompany]: 'errors.GroupIpiNotBelongsToCompany',
  [MetaErrorCodes.GroupPisCofinsNotBelongsToCompany]: 'errors.GroupPisCofinsNotBelongsToCompany'
}

export function useMapErrorMessage (error?: BaseError): string[] {
  if (error == null) {
    return []
  }

  let errors: string[] = []

  if (error.meta?.type) {
    return [error.meta.type]
  }

  if ((error.message?.trim().length ?? 0) > 0) {
    errors.push(error.message?.trim() ?? '')
  }

  if (error.meta != null) {
    errors = errors.concat(getMetaMessages(error.meta))
  }

  if (error.code !== ErrorCodes.Custom && (error.meta == null || errors.length === 0)) {
    errors.push(codeMessages[error.code])
  }

  if (errors.length === 0) {
    errors.push(codeMessages[ErrorCodes.Unknown])
  }

  return errors.filter(e => e != null)
}

function getMetaMessages (value: MetaErrorCodes | MetaErrorCodes[] | Record<any, MetaErrorCodes>): string[] {
  if (Array.isArray(value)) {
    return value.map(v => getMetaMessages(v)).flatMap(m => m)
  }

  if (typeof value === 'object') {
    return Object.values(value ?? [])
      .map(value => getMetaMessages(value))
      .flatMap(m => m)
      .filter(message => message != null)
      .filter((v, i, a) => a.indexOf(v) === i)
  }

  const mappedMessage = metaMessages[value as MetaErrorCodes]

  if (mappedMessage != null) {
    return [mappedMessage]
  }

  if (typeof value === 'string') {
    return [value]
  }

  return []
}
